/**
 * HRLabReport.jsx — Workforce Reaction Lab
 * 
 * Simulate tension. Detect friction. Design smarter decisions.
 * 
 * Architecture:
 * 1. Reaction Overview — Policy impact snapshot
 * 2. Friction Map — Ranked tension by segment
 * 3. Structural Drivers — Why resistance exists
 * 4. Risk Scenarios — What could go wrong
 * 5. Behavioral Archetypes — Population clusters (optional)
 * 6. Reliability & Assumptions — Collapsed
 *
 * No tabs. No clutter. No fragmentation.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Loader2,
  Settings2,
  Shield,
  Minus,
  Info,
  Zap,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { buildReliabilitySummary } from '../lib/reliabilityUtils';
import ReliabilitySummary from './reporting/ReliabilitySummary';
import SegmentDriverList from './reporting/SegmentDriverList';
import RecommendedActionsCard from './reporting/RecommendedActionsCard';
import ExecutiveSummaryCard from './reporting/ExecutiveSummaryCard';
import MetricStrip from './reporting/MetricStrip';
import AssumptionsDrawer from './reporting/AssumptionsDrawer';
import InsightCallout from './reporting/InsightCallout';

// =============================================================================
// DERIVED METRICS CALCULATOR
// =============================================================================

function computeLabMetrics(report) {
  if (!report) return null;

  // Try to get data from tiles first, fallback to signals_hotspots
  const tile = report.outcome_overview?.tiles?.[0];
  
  // If no tile data, try to derive from signals/hotspots
  let favorable = 0, neutral = 0, unfavorable = 0;
  let outcomeName = 'Policy Acceptance';
  
  if (tile) {
    favorable = tile.distribution?.favorable || 0;
    neutral = tile.distribution?.neutral || 0;
    unfavorable = tile.distribution?.unfavorable || 0;
    outcomeName = tile.display_name || 'Policy Acceptance';
  } else {
    // Fallback: derive from signals_hotspots critical items
    const signals = report.signals_hotspots || {};
    const criticalSignals = signals.critical || [];
    
    // Try to extract data from critical signals
    for (const signal of criticalSignals) {
      if (signal.metric_name === 'Low Favorable') {
        favorable = signal.metric_value || 20;
        // Estimate others
        unfavorable = Math.min(100 - favorable, 40);
        neutral = 100 - favorable - unfavorable;
        break;
      }
    }
    
    // If still no data, use reasonable defaults based on total_responses
    if (favorable === 0 && unfavorable === 0) {
      const totalResponses = report.outcome_overview?.summary?.total_responses || 0;
      if (totalResponses > 0) {
        favorable = 30;
        neutral = 40;
        unfavorable = 30;
      } else {
        return null; // No data at all
      }
    }
    
    outcomeName = 'Overall Response';
  }
  const total = favorable + neutral + unfavorable;

  // Net Reaction Score: favorable minus unfavorable (range: -100 to +100)
  const netReaction = Math.round(favorable - unfavorable);

  // Polarization Index: how split is the population?
  // High when both favorable AND unfavorable are significant, neutral is low
  // Scale 0-100
  const polarization = total > 0
    ? Math.round((Math.min(favorable, unfavorable) * 2 / total) * 100)
    : 0;

  // Resistance Intensity: combination of unfavorable % and concentration
  // Base = unfavorable %. Boost if frictions are concentrated in specific segments.
  const frictions = report.drivers_frictions?.frictions || [];
  const maxDeviation = frictions.length > 0
    ? Math.max(...frictions.map(f => Math.abs(f.deviation_pp || 0)))
    : 0;
  const resistanceBase = unfavorable;
  const resistanceBoost = Math.min(maxDeviation * 0.5, 20);
  const resistanceIntensity = Math.min(100, Math.round(resistanceBase + resistanceBoost));

  // Confidence from stability
  const stability = report.overall_stability || {};

  // Build friction map from segment_spotlight
  const segments = report.segment_spotlight?.top_segments || [];
  const frictionMap = segments
    .map(seg => ({
      segment: seg.value,
      dimension: seg.dimension,
      delta: seg.deviation_pp,
      size: seg.size,
      risk: Math.abs(seg.deviation_pp) >= 15 ? 'severe'
          : Math.abs(seg.deviation_pp) >= 10 ? 'high'
          : Math.abs(seg.deviation_pp) >= 5  ? 'medium'
          : 'low',
    }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  // Structural drivers: group by dimension, find max influence per dim
  const allItems = [
    ...(report.drivers_frictions?.drivers || []),
    ...(report.drivers_frictions?.frictions || []),
  ];
  const dimInfluence = {};
  for (const item of allItems) {
    // Extract dimension from "dimension: value" format
    const colonIdx = item.name?.indexOf(':');
    const dim = colonIdx > 0 ? item.name.substring(0, colonIdx).trim() : item.name;
    const current = dimInfluence[dim] || { maxDev: 0, count: 0, items: [] };
    current.maxDev = Math.max(current.maxDev, Math.abs(item.deviation_pp || 0));
    current.count += 1;
    current.items.push(item);
    dimInfluence[dim] = current;
  }
  const structuralDrivers = Object.entries(dimInfluence)
    .map(([dim, info]) => ({
      dimension: dim.replace(/_/g, ' '),
      rawDimension: dim,
      maxDeviation: info.maxDev,
      segmentCount: info.count,
      strength: info.maxDev >= 15 ? 'strong'
              : info.maxDev >= 10 ? 'moderate'
              : info.maxDev >= 5  ? 'weak'
              : 'minimal',
      items: info.items,
    }))
    .sort((a, b) => b.maxDeviation - a.maxDeviation);

  // Risk scenarios
  const signals = report.signals_hotspots || {};
  const criticalCount = signals.critical_count || 0;
  const watchCount = signals.watch_count || 0;

  const resistanceRisk = resistanceIntensity >= 60 ? 'high'
                       : resistanceIntensity >= 35 ? 'medium' : 'low';
  const engagementDropRisk = unfavorable >= 50 ? Math.round(unfavorable * 0.6)
                           : unfavorable >= 30 ? Math.round(unfavorable * 0.4)
                           : Math.round(unfavorable * 0.2);
  const fragmentationDetected = polarization >= 40 || criticalCount >= 2;

  // Generate the one sharp sentence
  let impactSentence = '';
  if (resistanceIntensity >= 60) {
    const topFriction = frictions[0];
    const segment = topFriction ? topFriction.name?.split(':')?.[1]?.trim() : null;
    impactSentence = segment
      ? `This policy generates significant resistance concentrated in ${segment}.`
      : 'This policy generates significant resistance across multiple segments.';
  } else if (resistanceIntensity >= 35) {
    const topFriction = frictions[0];
    const segment = topFriction ? topFriction.name?.split(':')?.[1]?.trim() : null;
    impactSentence = segment
      ? `This policy generates moderate resistance, particularly among ${segment}.`
      : 'This policy generates moderate resistance across the workforce.';
  } else if (netReaction > 20) {
    impactSentence = 'This policy is likely to be received positively with minimal friction.';
  } else {
    impactSentence = 'This policy generates mixed reactions. Monitor key segments before rollout.';
  }

  // Behavioral archetypes derived from segment clusters
  const archetypes = deriveArchetypes(frictionMap, favorable, unfavorable, neutral);

  return {
    support: favorable,
    favorablePct: favorable,
    unfavorablePct: unfavorable,
    neutralPct: neutral,
    netReaction,
    polarization,
    resistanceIntensity,
    stability,
    impactSentence,
    frictionMap,
    structuralDrivers,
    risks: {
      resistanceRisk,
      engagementDropPct: engagementDropRisk,
      fragmentationDetected,
    },
    archetypes,
    signals,
    totalResponses: report.outcome_overview?.summary?.total_responses || 0,
    outcomeName,
    coverageRate: report.outcome_overview?.summary?.coverage_rate ?? null,
    needsSetup: report.measurement_setup?.needs_setup || false,
  };
}

/**
 * derivePeopleLabActions — turns the precomputed lab metrics into concrete
 * manager actions matching the RecommendedActionsCard contract:
 *   { action, target, why, owner, timing, evidence, priority }
 */
function derivePeopleLabActions(lab) {
  if (!lab) return [];
  const actions = [];
  const frictionTop = (lab.frictionMap || []).slice(0, 2);
  const driversTop = (lab.structuralDrivers || []).slice(0, 2);
  const archetypes = lab.archetypes || [];
  const unfav = lab.unfavorablePct || 0;
  const fav = lab.favorablePct || 0;
  const neutral = lab.neutralPct || 0;
  const stabilityLevel = lab.stability?.level;

  // 1. Address the biggest friction segment
  if (frictionTop[0]) {
    const seg = frictionTop[0];
    const segLabel = `${seg.segment} (${(seg.dimension || '').replace(/_/g, ' ')})`;
    actions.push({
      action: `Run a focused listening session with ${segLabel}`,
      target: segLabel,
      why: `This segment deviates ${seg.delta > 0 ? '+' : ''}${seg.delta.toFixed(1)}pp from the baseline — by far the largest tension surfaced in this run.`,
      owner: 'HR Business Partner',
      timing: 'Within 2 weeks',
      evidence: [
        `Segment delta vs. baseline: ${seg.delta > 0 ? '+' : ''}${seg.delta.toFixed(1)}pp (n=${seg.size})`,
        seg.risk ? `Friction risk classified as ${seg.risk}` : null,
      ].filter(Boolean),
      priority: seg.risk === 'severe' || Math.abs(seg.delta) >= 15 ? 'high' : 'medium',
    });
  }

  // 2. Communicate with detractors
  if (unfav >= 30 || archetypes.find((a) => a.name === 'Detractors')?.size >= 0.25) {
    actions.push({
      action: 'Pre-empt detractor narratives with a clear FAQ',
      target: 'Detractors / unfavorable cohort',
      why: `~${unfav.toFixed(0)}% of respondents reacted unfavorably. Without proactive framing, they will likely shape the rollout narrative.`,
      owner: 'Internal Communications + HR Lead',
      timing: 'Pre-launch',
      evidence: [`Unfavorable share: ${unfav.toFixed(0)}%`, `Favorable share: ${fav.toFixed(0)}%`],
      priority: unfav >= 40 ? 'high' : 'medium',
    });
  }

  // 3. Convert the neutral middle
  if (neutral >= 25) {
    actions.push({
      action: 'Design a targeted nudge for the undecided middle',
      target: `Neutral / undecided (${neutral.toFixed(0)}%)`,
      why: 'A meaningful neutral cohort means messaging tweaks can move the needle without requiring deep change-management work.',
      owner: 'People Analytics',
      timing: 'Within 4 weeks',
      evidence: [`Neutral share: ${neutral.toFixed(0)}%`],
      priority: 'medium',
    });
  }

  // 4. Lean into structural drivers — pick the strongest *positive* individual segment from frictionMap
  const championSeg = (lab.frictionMap || [])
    .filter((s) => (s.delta || 0) > 5 && !s.masked)
    .sort((a, b) => (b.delta || 0) - (a.delta || 0))[0];
  if (championSeg) {
    actions.push({
      action: `Use ${championSeg.segment} as a launch champion cohort`,
      target: `${championSeg.segment} (${(championSeg.dimension || '').replace(/_/g, ' ')})`,
      why: `This group leads support by +${(championSeg.delta || 0).toFixed(1)}pp — they can model the change and serve as a trust anchor for peers.`,
      owner: 'Change Lead',
      timing: 'Pre-launch',
      evidence: [`Driver delta: +${(championSeg.delta || 0).toFixed(1)}pp${championSeg.size != null ? ` (n=${championSeg.size})` : ''}`],
      priority: 'medium',
    });
  } else if (driversTop[0] && driversTop[0].dimension) {
    // Fallback — recommend a deeper look at the strongest dimension
    const d = driversTop[0];
    actions.push({
      action: `Investigate ${d.dimension} as a structural driver`,
      target: `${d.dimension} dimension`,
      why: `This dimension shows the largest swing in support across its segments (max deviation ${(d.maxDeviation || 0).toFixed(1)}pp).`,
      owner: 'People Analytics',
      timing: 'Within 4 weeks',
      evidence: [`Strongest dimension: ${d.dimension} (${d.strength})`, `Max deviation across segments: ${(d.maxDeviation || 0).toFixed(1)}pp`],
      priority: 'medium',
    });
  }

  // 5. Reliability-driven action — only trigger when there is a real concern.
  const knownLowCoverage = typeof lab.coverageRate === 'number' && lab.coverageRate < 0.9;
  if (stabilityLevel === 'low' || knownLowCoverage) {
    actions.push({
      action: 'Validate findings with a follow-up study before broad action',
      target: 'Decision-makers reviewing this report',
      why:
        stabilityLevel === 'low'
          ? 'Overall stability is low — segment numbers may shift under small assumption changes.'
          : 'Coverage is below 90%, so segment-level conclusions should be confirmed against the full population.',
      owner: 'People Analytics',
      timing: 'Before sharing externally',
      evidence: [
        stabilityLevel ? `Stability level: ${stabilityLevel}` : null,
        knownLowCoverage ? `Coverage rate: ${(lab.coverageRate * 100).toFixed(0)}%` : null,
      ].filter(Boolean),
      priority: 'high',
    });
  }

  return actions;
}



function deriveArchetypes(frictionMap, favorable, unfavorable, neutral) {
  // Estimate population clusters from response distribution + segment behavior
  const archetypes = [];

  // Supporters - those who respond favorably
  if (favorable > 10) {
    const positiveSegments = frictionMap.filter(s => s.delta > 5);
    archetypes.push({
      name: 'Supporters',
      pct: Math.round(favorable * 0.7),
      tendency: 'Favorable reaction, likely early adopters',
      strategy: 'Leverage as internal champions. Involve in rollout communication.',
      segments: positiveSegments.slice(0, 2).map(s => s.segment),
    });
  }

  // Quiet Resisters - neutral responders who lean negative in key segments
  if (neutral > 15) {
    archetypes.push({
      name: 'Quiet Resisters',
      pct: Math.round(neutral * 0.5),
      tendency: 'Neutral on surface, passive non-compliance risk',
      strategy: 'Provide clear rationale and address concerns early. Track adoption metrics.',
      segments: [],
    });
  }

  // Active Opposers - those responding unfavorably
  if (unfavorable > 15) {
    const negativeSegments = frictionMap.filter(s => s.delta < -10);
    archetypes.push({
      name: 'Active Opposers',
      pct: Math.round(unfavorable * 0.6),
      tendency: 'Vocal resistance, may influence peers',
      strategy: 'Engage directly. Understand concerns. Consider targeted accommodations.',
      segments: negativeSegments.slice(0, 2).map(s => s.segment),
    });
  }

  // Fence-Sitters
  const fenceSitterPct = 100 - archetypes.reduce((sum, a) => sum + a.pct, 0);
  if (fenceSitterPct > 5) {
    archetypes.push({
      name: 'Fence-Sitters',
      pct: Math.max(fenceSitterPct, 5),
      tendency: 'Undecided, influenced by peer behavior and communication',
      strategy: 'Target with clear messaging. Peer testimonials effective.',
      segments: [],
    });
  }

  return archetypes;
}


// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function HRLabReport({ simulationId, simulation }) {
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [preReliability, setPreReliability] = useState(null);

  const effectiveSimulationId = simulationId || simulation?.id;

  const fetchReport = useCallback(async () => {
    if (!effectiveSimulationId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/hrlab/report/${effectiveSimulationId}/full`);
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [effectiveSimulationId, token]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Fetch PRE analytics in the background when simulation was run with PRE.
  // Errors are silently ignored — the unified panel falls back to PeopleLab stability only.
  useEffect(() => {
    if (!effectiveSimulationId) return;
    const preEnabled =
      simulation?.pre_mode_used ||
      simulation?.config?.use_pre ||
      simulation?.config?.pre_mode_enabled ||
      simulation?.output_mode === 'probs';
    if (!preEnabled) {
      setPreReliability(null);
      return;
    }
    let cancelled = false;
    api
      .get(`/simulations/${effectiveSimulationId}/pre-analytics`, { _silent: true })
      .then((res) => { if (!cancelled) setPreReliability(res.data); })
      .catch(() => { if (!cancelled) setPreReliability(null); });
    return () => { cancelled = true; };
  }, [
    effectiveSimulationId,
    simulation?.pre_mode_used,
    simulation?.config?.use_pre,
    simulation?.config?.pre_mode_enabled,
    simulation?.output_mode,
  ]);

  const lab = useMemo(() => computeLabMetrics(report), [report]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Running workforce reaction analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-slate-200 dark:border-border rounded-lg">
        <p className="text-sm text-slate-600 dark:text-slate-300">{error}</p>
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="p-4 border border-slate-200 dark:border-border rounded-lg">
        <p className="text-sm text-slate-500 dark:text-slate-400">No reaction data available for this simulation.</p>
      </div>
    );
  }

  const needsSetup = report?.measurement_setup?.needs_setup;

  return (
    <div className="space-y-6" data-testid="hrlab-report">
      {/* Setup Warning Banner with Quick Setup CTA */}
      {lab.needsSetup && (
        <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-warning-border rounded-xl">
          <Zap className="w-5 h-5 text-warning-soft-foreground mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-warning-soft-foreground">Quick Setup Available</p>
            <p className="text-xs text-warning-soft-foreground mt-1">
              We can auto-detect HR outcome mappings from your stimuli. Run the Quick Setup Wizard to unlock accurate metrics.
            </p>
          </div>
          <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white">
                <Zap className="w-4 h-4 mr-1.5" />
                Quick Setup
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
              <MeasurementSetupDialog
                measurementSetup={report?.measurement_setup}
                simulationId={effectiveSimulationId}
                onComplete={() => { setSetupDialogOpen(false); fetchReport(); }}
              />
            </DialogContent>
          </Dialog>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            Workforce Reaction Lab
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {report?.simulation_name} &middot; {lab.totalResponses} simulated responses
          </p>
        </div>
      </div>

      {/* Report metadata — context influence + workforce coverage */}
      <PeopleLabMetadataStrip simulation={simulation} />

      {/* 0. EXECUTIVE SUMMARY — boardroom decision card */}
      <ExecutiveSummary lab={lab} simulation={simulation} preData={preReliability} diagnostics={report?.diagnostics} />

      {/* 1. REACTION OVERVIEW */}
      <ReactionOverview lab={lab} />

      {/* 2. FRICTION MAP */}
      <FrictionMap segments={lab.frictionMap} />

      {/* 3. STRUCTURAL DRIVERS */}
      <StructuralDrivers drivers={lab.structuralDrivers} />

      {/* 4. RISK SCENARIOS */}
      <RiskScenarios risks={lab.risks} signals={lab.signals} />

      {/* 4b. HR-ORIENTED ANALYTICS */}
      <HRAnalytics lab={lab} frictionMap={lab.frictionMap} structuralDrivers={lab.structuralDrivers} />

      {/* 5. BEHAVIORAL ARCHETYPES */}
      <BehavioralArchetypes archetypes={lab.archetypes} />

      {/* 5b. RECOMMENDED MANAGER ACTIONS */}
      <RecommendedActionsCard
        title="Recommended Manager Actions"
        subtitle="Concrete next steps derived from this report"
        actions={derivePeopleLabActions(lab)}
        testId="peoplelab-recommended-actions"
      />

      {/* 6. RELIABILITY & ASSUMPTIONS (collapsed) */}
      <ReliabilityPanel
        stability={lab.stability}
        diagnostics={report?.diagnostics}
        simulation={simulation}
        preData={preReliability}
      />

      {/* 7. METHOD & ASSUMPTIONS DRAWER */}
      <AssumptionsDrawer simulation={simulation} testId="peoplelab-assumptions-drawer" />
    </div>
  );
}


// =============================================================================
// 1. REACTION OVERVIEW
// =============================================================================

function ReactionOverview({ lab }) {
  const { support, netReaction, polarization, resistanceIntensity, stability, impactSentence, outcomeName } = lab;

  const metrics = [
    {
      label: 'Overall Support',
      value: `${support.toFixed(0)}%`,
      sublabel: outcomeName,
      tone: support >= 55 ? 'positive' : support >= 40 ? 'neutral' : 'negative',
    },
    {
      label: 'Net Reaction',
      value: netReaction > 0 ? `+${netReaction}` : `${netReaction}`,
      sublabel: 'favorable − unfavorable',
      tone: netReaction > 10 ? 'positive' : netReaction >= -10 ? 'neutral' : 'negative',
    },
    {
      label: 'Polarization',
      value: `${polarization}%`,
      sublabel: polarization >= 40 ? 'High split' : polarization >= 20 ? 'Moderate split' : 'Low split',
      tone: polarization >= 40 ? 'negative' : polarization >= 20 ? 'neutral' : 'positive',
    },
    {
      label: 'Resistance',
      value: resistanceIntensity >= 60 ? 'High' : resistanceIntensity >= 35 ? 'Moderate' : 'Low',
      sublabel: `${resistanceIntensity}% intensity`,
      tone: resistanceIntensity >= 60 ? 'negative' : resistanceIntensity >= 35 ? 'neutral' : 'positive',
    },
    {
      label: 'Confidence',
      value: stability.level === 'high' ? 'High' : stability.level === 'medium' ? 'Medium' : 'Low',
      sublabel: stability.reason,
      tone: stability.level === 'high' ? 'positive' : stability.level === 'medium' ? 'neutral' : 'negative',
    },
  ];

  // What-to-notice caption
  let captionTone = 'neutral';
  let captionText = impactSentence;
  if (resistanceIntensity >= 60) captionTone = 'negative';
  else if (resistanceIntensity < 35 && netReaction > 10) captionTone = 'positive';
  else if (polarization >= 40) captionTone = 'warning';

  return (
    <section data-testid="reaction-overview" className="space-y-3">
      <SectionHeader title="Reaction Overview" subtitle="How the workforce responded, at a glance" />
      <Card className="border border-slate-200 dark:border-border">
        <CardContent className="p-6 space-y-4">
          <p className="text-base text-slate-800 dark:text-slate-100 font-medium leading-relaxed">
            {impactSentence}
          </p>
          <MetricStrip metrics={metrics} testId="reaction-overview-metrics" />
          <InsightCallout
            label="What to notice"
            text={captionText}
            tone={captionTone}
            testId="reaction-overview-callout"
          />
        </CardContent>
      </Card>
    </section>
  );
}

// =============================================================================
// 0. EXECUTIVE SUMMARY (one-line conclusion + top friction + next move)
// =============================================================================

function ExecutiveSummary({ lab, simulation, preData, diagnostics }) {
  if (!lab) return null;

  const { impactSentence, frictionMap, resistanceIntensity, netReaction, polarization } = lab;

  // Top friction line
  let frictionLabel = null;
  let frictionDetail = null;
  if (frictionMap && frictionMap.length > 0) {
    const top = frictionMap.reduce((a, b) => (Math.abs(b.delta) > Math.abs(a.delta) ? b : a));
    if (Math.abs(top.delta) >= 5) {
      const dim = (top.dimension || '').replace(/_/g, ' ');
      frictionLabel = `${top.segment}${dim ? ` · ${dim}` : ''}`;
      const sign = top.delta > 0 ? '+' : '';
      frictionDetail = `${sign}${top.delta.toFixed(1)}pp vs baseline${top.size != null ? ` (n=${top.size})` : ''}`;
    } else {
      frictionLabel = 'No significant segment-level friction';
      frictionDetail = 'All groups respond within 5pp of the overall baseline.';
    }
  }

  // Next move heuristic
  let nextMove;
  if (resistanceIntensity >= 60) {
    nextMove = 'Pause broad rollout. Run a focused listening session with the top friction segment before launch.';
  } else if (resistanceIntensity >= 35 || polarization >= 40) {
    nextMove = 'Phase the rollout. Pre-empt detractor narratives and target the undecided middle with clear messaging.';
  } else if (netReaction > 15) {
    nextMove = 'Proceed with rollout. Use the strongest support segment as a launch champion.';
  } else {
    nextMove = 'Validate findings with a follow-up signal before committing to action.';
  }

  // Tone
  let tone = 'neutral';
  if (resistanceIntensity >= 60) tone = 'negative';
  else if (resistanceIntensity < 35 && netReaction > 10) tone = 'positive';
  else if (polarization >= 40) tone = 'warning';

  return (
    <ExecutiveSummaryCard
      conclusion={impactSentence}
      frictionLabel={frictionLabel}
      frictionDetail={frictionDetail}
      nextMove={nextMove}
      tone={tone}
      simulation={simulation}
      preData={preData}
      peopleLabStability={lab.stability}
      diagnostics={diagnostics}
      badgeLabel="Executive Decision · PeopleLab"
      testId="peoplelab-executive-summary"
    />
  );
}

function MetricCell({ label, value, sub, tone }) {
  const toneColors = {
    positive: 'text-positive-soft-foreground ',
    neutral: 'text-warning-soft-foreground ',
    negative: 'text-negative-soft-foreground ',
  };

  return (
    <div className="bg-white dark:bg-card p-4 text-center" data-testid="metric-cell">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1">{label}</p>
      <p className={cn("text-xl font-bold tabular-nums", toneColors[tone] || 'text-slate-800 dark:text-slate-100')}>
        {value}
      </p>
      <p className="text-[10px] text-slate-400 mt-1 truncate" title={sub}>{sub}</p>
    </div>
  );
}


// =============================================================================
// 2. FRICTION MAP
// =============================================================================

function FrictionMap({ segments }) {
  if (!segments || segments.length === 0) {
    return (
      <section data-testid="friction-map">
        <SectionHeader title="Friction Map" />
        <SegmentDriverList
          items={[]}
          emptyMessage="No significant friction points detected in this simulation"
          emptyHint="Friction points appear when specific demographic segments deviate significantly (5pp+) from the baseline response. Uniform responses across all segments indicate the stimulus is perceived similarly by the entire population."
          testId="friction-map-list"
        />
      </section>
    );
  }

  return (
    <section data-testid="friction-map">
      <SectionHeader title="Friction Map" subtitle="Tension by segment, ranked by deviation from baseline" />
      <SegmentDriverList items={segments.slice(0, 8)} mode="cards" testId="friction-map-list" />
    </section>
  );
}


// =============================================================================
// 3. STRUCTURAL DRIVERS
// =============================================================================

function StructuralDrivers({ drivers }) {
  if (!drivers || drivers.length === 0) {
    return (
      <section data-testid="structural-drivers">
        <SectionHeader title="Structural Drivers" />
        <div className="p-5 bg-slate-50 dark:bg-slate-800/30 rounded-lg border border-slate-100 dark:border-slate-700">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">No structural demographic drivers detected</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                Structural drivers emerge when a demographic dimension (e.g., leadership level, department, tenure)
                consistently explains variation in responses. This means no single demographic attribute strongly
                predicts how people will react to this stimulus.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Find interaction highlights: pairs of dimensions that combine for extreme results
  const interactions = [];
  for (let i = 0; i < drivers.length; i++) {
    for (let j = i + 1; j < drivers.length; j++) {
      if (drivers[i].maxDeviation >= 10 && drivers[j].maxDeviation >= 10) {
        interactions.push({
          dims: [drivers[i].dimension, drivers[j].dimension],
          combined: Math.round(drivers[i].maxDeviation + drivers[j].maxDeviation * 0.5),
        });
      }
    }
  }

  return (
    <section data-testid="structural-drivers">
      <SectionHeader title="Structural Drivers" subtitle="Which demographic dimensions drive reaction differences" />

      <div className="space-y-2">
        {drivers.map((d, i) => (
          <div key={i} className="flex items-center gap-4 py-3 px-4 bg-white dark:bg-card border border-slate-100 dark:border-border rounded-lg">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800 dark:text-slate-100 capitalize">{d.dimension}</span>
                <StrengthBadge strength={d.strength} />
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {d.segmentCount} segment{d.segmentCount !== 1 ? 's' : ''} with significant deviation
              </p>
            </div>
            {/* Influence bar */}
            <div className="w-32 flex items-center gap-2">
              <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    d.strength === 'strong' ? 'bg-negative' :
                    d.strength === 'moderate' ? 'bg-warning' :
                    'bg-slate-300'
                  )}
                  style={{ width: `${Math.min((d.maxDeviation / 25) * 100, 100)}%` }}
                />
              </div>
              <span className="text-xs font-mono text-slate-500 w-8 text-right">{d.maxDeviation.toFixed(0)}pp</span>
            </div>
          </div>
        ))}
      </div>

      {interactions.length > 0 && (
        <div className="mt-3 p-3 bg-slate-50 dark:bg-muted/50 border border-slate-100 dark:border-border rounded-lg">
          <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Interaction Highlights</p>
          {interactions.slice(0, 2).map((inter, i) => (
            <p key={i} className="text-xs text-slate-500">
              <span className="font-medium capitalize">{inter.dims[0]}</span>
              {' + '}
              <span className="font-medium capitalize">{inter.dims[1]}</span>
              {' = potential extreme resistance cluster'}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}


// =============================================================================
// 4. RISK SCENARIOS
// =============================================================================

function RiskScenarios({ risks, signals }) {
  const { resistanceRisk, engagementDropPct, fragmentationDetected } = risks;

  return (
    <section data-testid="risk-scenarios">
      <SectionHeader title="Risk Scenarios" subtitle="What could go wrong if this policy is implemented" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RiskCard
          title="Active Resistance"
          level={resistanceRisk}
          detail={
            resistanceRisk === 'high' ? 'Significant push-back expected. Plan change management.'
            : resistanceRisk === 'medium' ? 'Some vocal opposition likely. Monitor early signals.'
            : 'Minimal organized resistance expected.'
          }
        />
        <RiskCard
          title="Engagement Drop"
          level={engagementDropPct >= 25 ? 'high' : engagementDropPct >= 10 ? 'medium' : 'low'}
          detail={`Estimated ${engagementDropPct}% engagement impact. ${engagementDropPct >= 20 ? 'Consider phased rollout.' : 'Within acceptable range.'}`}
        />
        <RiskCard
          title="Cultural Fragmentation"
          level={fragmentationDetected ? 'high' : 'low'}
          detail={
            fragmentationDetected
              ? 'Polarized response detected. Risk of creating in-group/out-group dynamics.'
              : 'No significant cultural fracture detected.'
          }
        />
      </div>
    </section>
  );
}

function RiskCard({ title, level, detail }) {
  const config = {
    high: { border: 'border-l-red-500', bg: 'bg-negative-soft/30 ', text: 'text-negative-soft-foreground ', label: 'High' },
    medium: { border: 'border-l-amber-400', bg: 'bg-warning-soft/30 ', text: 'text-warning-soft-foreground ', label: 'Medium' },
    low: { border: 'border-l-slate-300 dark:border-l-slate-600', bg: 'bg-white dark:bg-card', text: 'text-slate-500 dark:text-slate-400', label: 'Low' },
  };
  const c = config[level] || config.low;

  return (
    <div className={cn("border border-slate-200 dark:border-border rounded-lg p-4 border-l-4", c.border, c.bg)} data-testid="risk-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</p>
        <Badge variant="outline" className={cn("text-[10px] font-semibold", c.text)}>
          {c.label}
        </Badge>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">{detail}</p>
    </div>
  );
}


// =============================================================================
// 5. BEHAVIORAL ARCHETYPES
// =============================================================================

function BehavioralArchetypes({ archetypes }) {
  const [expanded, setExpanded] = useState(false);

  if (!archetypes || archetypes.length === 0) return null;

  return (
    <section data-testid="behavioral-archetypes">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between cursor-pointer group">
            <SectionHeader title="Behavioral Archetypes" subtitle="Estimated population clusters and communication strategies" />
            <Button variant="ghost" size="sm" className="text-slate-400 group-hover:text-slate-600">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {archetypes.map((a, i) => (
              <div key={i} className="border border-slate-200 dark:border-border rounded-lg p-4 bg-white dark:bg-card" data-testid="archetype-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-sm text-slate-800 dark:text-slate-100">{a.name}</p>
                  <span className="text-xs font-mono font-semibold text-slate-500">~{a.pct}%</span>
                </div>
                <p className="text-xs text-slate-500 mb-2">{a.tendency}</p>
                <div className="pt-2 border-t border-slate-50 dark:border-border">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1">Strategy</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">{a.strategy}</p>
                </div>
                {a.segments?.length > 0 && (
                  <div className="flex gap-1.5 mt-2">
                    {a.segments.map((s, j) => (
                      <Badge key={j} variant="outline" className="text-[10px]">{s}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}


// =============================================================================
// HR-ORIENTED ANALYTICS
// =============================================================================

function HRAnalytics({ lab, frictionMap, structuralDrivers }) {
  // Derive HR-specific analytics from existing data
  const { support, resistanceIntensity, polarization, netReaction, archetypes } = lab;

  // 1. Adoption Risk Segments — segments most likely to resist adoption
  const adoptionRiskSegments = (frictionMap || [])
    .filter(s => s.delta < -5)
    .map(s => ({
      ...s,
      adoptionRisk: Math.abs(s.delta) >= 15 ? 'High' : Math.abs(s.delta) >= 10 ? 'Medium' : 'Low',
    }))
    .slice(0, 5);

  // 2. Policy Resistance Groups — aggregated by dimension
  const resistanceGroups = (structuralDrivers || [])
    .filter(d => d.maxDeviation >= 5)
    .map(d => ({
      ...d,
      resistanceLevel: d.maxDeviation >= 15 ? 'Strong' : d.maxDeviation >= 10 ? 'Moderate' : 'Mild',
    }))
    .slice(0, 4);

  // 3. Organizational Alignment Score
  const alignmentScore = Math.max(0, Math.min(100, Math.round(
    (support * 0.4) + ((100 - polarization) * 0.3) + ((100 - resistanceIntensity) * 0.3)
  )));
  const alignmentLabel = alignmentScore >= 70 ? 'Strong' : alignmentScore >= 45 ? 'Moderate' : 'Weak';
  const alignmentColor = alignmentScore >= 70 ? 'text-positive-soft-foreground' : alignmentScore >= 45 ? 'text-warning-soft-foreground' : 'text-negative-soft-foreground';
  const alignmentBg = alignmentScore >= 70 ? 'bg-positive-soft' : alignmentScore >= 45 ? 'bg-warning-soft' : 'bg-negative-soft';

  // 4. Leadership Trust Indicator (derived from leadership-related segments)
  const leadershipSegments = (frictionMap || []).filter(
    s => s.dimension?.toLowerCase().includes('leadership') || s.dimension?.toLowerCase().includes('manager')
  );
  const leadershipDelta = leadershipSegments.length > 0
    ? leadershipSegments.reduce((s, seg) => s + seg.delta, 0) / leadershipSegments.length
    : 0;
  const trustLabel = leadershipDelta > 5 ? 'High Trust' : leadershipDelta > -5 ? 'Neutral' : 'Trust Gap';
  const trustColor = leadershipDelta > 5 ? 'text-positive-soft-foreground' : leadershipDelta > -5 ? 'text-slate-600' : 'text-negative-soft-foreground';

  return (
    <section data-testid="hr-analytics">
      <SectionHeader title="HR Decision Intelligence" subtitle="Key workforce analytics for evidence-based decision making" />

      {/* Organizational Alignment Score - hero metric */}
      <Card className="border border-slate-200 dark:border-slate-700 mb-4" data-testid="alignment-score">
        <CardContent className="p-5">
          <div className="flex items-center gap-6">
            <div className="flex-shrink-0 text-center">
              <div className={cn("w-20 h-20 rounded-full flex items-center justify-center border-4", alignmentBg,
                alignmentScore >= 70 ? 'border-positive-border' : alignmentScore >= 45 ? 'border-warning-border' : 'border-negative-border'
              )}>
                <span className={cn("text-2xl font-black tabular-nums", alignmentColor)}>{alignmentScore}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">/ 100</p>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Organizational Alignment</h4>
                <Badge variant="outline" className={cn("text-[10px] font-semibold", alignmentColor)}>
                  {alignmentLabel}
                </Badge>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                {alignmentScore >= 70
                  ? 'The workforce shows strong alignment with this initiative. Broad support and low resistance indicate readiness for implementation.'
                  : alignmentScore >= 45
                  ? 'Moderate alignment detected. Some segments show resistance — targeted communication and phased rollout recommended.'
                  : 'Weak organizational alignment. Significant resistance and polarization suggest this initiative needs rethinking or extensive change management.'}
              </p>
              <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-400">
                <span>Support: {support?.toFixed(0) || 0}%</span>
                <span>Polarization: {polarization}%</span>
                <span>Net Reaction: {netReaction > 0 ? '+' : ''}{netReaction}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Adoption Risk Segments */}
        <Card className="border border-slate-200 dark:border-slate-700" data-testid="adoption-risk">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-negative" />
              Adoption Risk Segments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {adoptionRiskSegments.length > 0 ? (
              <div className="space-y-2">
                {adoptionRiskSegments.map((seg, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-700">
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{seg.segment}</p>
                      <p className="text-[10px] text-slate-400">{seg.dimension?.replace(/_/g, ' ')} &middot; n={seg.size}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className={cn("text-[10px] font-semibold",
                        seg.adoptionRisk === 'High' ? 'text-negative-soft-foreground bg-negative-soft border-negative-border' :
                        seg.adoptionRisk === 'Medium' ? 'text-warning-soft-foreground bg-warning-soft border-warning-border' :
                        'text-slate-500 bg-slate-50 border-slate-200'
                      )}>
                        {seg.adoptionRisk} Risk
                      </Badge>
                      <p className="text-[10px] font-mono text-negative mt-0.5">{seg.delta.toFixed(1)}pp</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500 py-3">No high-risk adoption segments detected. All groups show acceptable adoption likelihood.</p>
            )}
          </CardContent>
        </Card>

        {/* Policy Resistance Groups */}
        <Card className="border border-slate-200 dark:border-slate-700" data-testid="resistance-groups">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield className="w-4 h-4 text-warning" />
              Policy Resistance by Dimension
            </CardTitle>
          </CardHeader>
          <CardContent>
            {resistanceGroups.length > 0 ? (
              <div className="space-y-2">
                {resistanceGroups.map((grp, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5 px-3 rounded-lg bg-slate-50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-700">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 capitalize">{grp.dimension}</p>
                      <p className="text-[10px] text-slate-400">{grp.segmentCount} affected segment{grp.segmentCount > 1 ? 's' : ''}</p>
                    </div>
                    <div className="w-20 flex items-center gap-1.5">
                      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className={cn("h-full rounded-full",
                          grp.resistanceLevel === 'Strong' ? 'bg-negative' :
                          grp.resistanceLevel === 'Moderate' ? 'bg-warning' : 'bg-slate-400'
                        )} style={{ width: `${Math.min((grp.maxDeviation / 25) * 100, 100)}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-slate-500 w-6 text-right">{grp.maxDeviation.toFixed(0)}</span>
                    </div>
                    <Badge variant="outline" className={cn("text-[10px]",
                      grp.resistanceLevel === 'Strong' ? 'text-negative-soft-foreground bg-negative-soft border-negative-border' :
                      grp.resistanceLevel === 'Moderate' ? 'text-warning-soft-foreground bg-warning-soft border-warning-border' :
                      'text-slate-500 bg-slate-50 border-slate-200'
                    )}>
                      {grp.resistanceLevel}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500 py-3">No significant resistance patterns detected across demographic dimensions.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Leadership Trust + Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Card className="border border-slate-200 dark:border-slate-700" data-testid="leadership-trust">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center",
                leadershipDelta > 5 ? 'bg-positive-soft ' : leadershipDelta > -5 ? 'bg-slate-50 dark:bg-slate-800' : 'bg-negative-soft '
              )}>
                <ArrowUp className={cn("w-5 h-5",
                  leadershipDelta > 5 ? 'text-positive-soft-foreground' : leadershipDelta > -5 ? 'text-slate-400' : 'text-negative rotate-180'
                )} />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">Leadership Trust</p>
                <p className={cn("text-lg font-bold", trustColor)}>{trustLabel}</p>
                {leadershipSegments.length > 0 ? (
                  <p className="text-[10px] text-slate-400">
                    Avg delta: {leadershipDelta > 0 ? '+' : ''}{leadershipDelta.toFixed(1)}pp across {leadershipSegments.length} leadership segment{leadershipSegments.length > 1 ? 's' : ''}
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-400">No leadership-level data in current breakdown</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Summary */}
        <Card className="border border-slate-200 dark:border-slate-700" data-testid="hr-summary">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-2">Decision Summary</p>
            <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
              {alignmentScore >= 70
                ? `This initiative has strong organizational alignment (${alignmentScore}/100). ${adoptionRiskSegments.length === 0 ? 'No significant adoption risks detected.' : `Monitor ${adoptionRiskSegments.length} at-risk segment${adoptionRiskSegments.length > 1 ? 's' : ''} during rollout.`}`
                : alignmentScore >= 45
                ? `Moderate alignment (${alignmentScore}/100) with ${adoptionRiskSegments.length} segment${adoptionRiskSegments.length > 1 ? 's' : ''} at adoption risk. Phased rollout with targeted communication recommended.`
                : `Low alignment score (${alignmentScore}/100). Consider redesigning the initiative or implementing extensive change management before rollout.`}
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}



// =============================================================================
// 6. SIMULATION RELIABILITY
// =============================================================================

function PeopleLabMetadataStrip({ simulation }) {
  if (!simulation) return null;
  const config = simulation?.config || {};
  const contextMode = config.context_mode || 'standard';
  const actualN = simulation.actual_n;
  const totalN = simulation.population_total_n;
  const coverageRate = simulation.coverage_rate;
  const isFull = !!simulation.is_full_census;
  const incomplete = !!simulation.incomplete_census_flag;

  const hasContext = !!contextMode;
  const hasCoverage = actualN != null && totalN != null;
  if (!hasContext && !hasCoverage) return null;

  const contextCls =
    contextMode === 'standard' ? 'bg-slate-50 text-slate-700 border-slate-200' :
    contextMode === 'light' ? 'bg-blue-50 text-blue-700 border-blue-200' :
    'bg-zinc-50 text-zinc-700 border-zinc-200';
  const contextLabel =
    contextMode === 'standard' ? 'Standard organizational context' :
    contextMode === 'light' ? 'Light context (background only)' :
    'No organizational context';
  const contextHelp =
    contextMode === 'standard' ? 'Company context is used as decision conditioning alongside individual profiles.' :
    contextMode === 'light' ? 'Company context is passed as background only — employees\u2019 profiles drive the answer.' :
    'Only the stimulus and individual profile were used; no organizational context was applied.';

  const covPct = coverageRate != null
    ? (coverageRate * 100).toFixed(1)
    : hasCoverage ? ((actualN / Math.max(totalN, 1)) * 100).toFixed(1) : null;
  const covCls = incomplete
    ? 'bg-warning-soft text-warning-soft-foreground border-warning-border'
    : isFull
    ? 'bg-positive-soft text-positive-soft-foreground border-positive-border'
    : 'bg-slate-50 text-slate-700 border-slate-200';
  const covLabel = isFull && !incomplete
    ? 'Full workforce census'
    : incomplete
    ? 'Incomplete workforce census'
    : 'Workforce sample';

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="peoplelab-metadata-strip">
      {hasContext && (
        <Badge
          variant="outline"
          className={cn('border px-2.5 py-1 text-[11px] font-medium', contextCls)}
          title={contextHelp}
          data-testid="peoplelab-context-mode"
        >
          Context: {contextLabel}
        </Badge>
      )}
      {hasCoverage && (
        <Badge
          variant="outline"
          className={cn('border px-2.5 py-1 text-[11px] font-medium', covCls)}
          title={`${actualN} of ${totalN} employees simulated`}
          data-testid="peoplelab-coverage"
        >
          {covLabel}: {actualN}/{totalN} ({covPct}%)
        </Badge>
      )}
      {incomplete && (
        <span className="text-[10px] text-warning-soft-foreground" data-testid="peoplelab-incomplete-note">
          Intended to simulate the full workforce but fewer employees were processed — interpret segment-level findings with caution.
        </span>
      )}
    </div>
  );
}

function ReliabilityPanel({ stability, diagnostics, simulation, preData }) {
  const [open, setOpen] = useState(false);

  // Build the unified reliability summary from the same shared utility used by the
  // generic Reliability tab. No fabricated numbers — every score is derived from
  // backend-provided data, with PRE evidence merged in when available.
  const summary = useMemo(
    () =>
      buildReliabilitySummary({
        simulation,
        preData,
        peopleLabStability: stability,
        diagnostics,
      }),
    [simulation, preData, stability, diagnostics]
  );

  const levelBadgeCls =
    summary.level === 'high'
      ? 'text-positive-soft-foreground bg-positive-soft border-positive-border'
      : summary.level === 'medium'
      ? 'text-warning-soft-foreground bg-warning-soft border-warning-border'
      : summary.level === 'low'
      ? 'text-negative-soft-foreground bg-negative-soft border-negative-border'
      : 'text-slate-600 bg-slate-50 border-slate-200';

  const headlineColor =
    summary.level === 'high'
      ? 'text-positive'
      : summary.level === 'medium'
      ? 'text-warning'
      : summary.level === 'low'
      ? 'text-negative'
      : 'text-slate-500';

  const hasPre = !!preData?.has_pre_data;

  return (
    <section data-testid="reliability-panel">
      <Collapsible open={open} onOpenChange={setOpen}>
        <Card className="border border-slate-100 dark:border-slate-700">
          <CollapsibleTrigger asChild>
            <CardHeader className="py-3 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-slate-400" />
                  <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    Reliability &amp; Assumptions
                  </CardTitle>
                  <Badge
                    variant="outline"
                    className={cn('text-[10px] font-semibold capitalize', levelBadgeCls)}
                    data-testid="reliability-overall-level"
                  >
                    {summary.label}
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex items-center gap-2 text-[11px] text-slate-500">
                    <span>Report Reliability:</span>
                    <span className={cn('font-semibold tabular-nums', headlineColor)} data-testid="reliability-overall-score">
                      {summary.score != null ? `${summary.score} / 100` : '—'}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    {open ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              {/* Shared 4-pillar reliability summary — same component used everywhere */}
              <ReliabilitySummary
                mode="full"
                simulation={simulation}
                preData={preData}
                peopleLabStability={stability}
                diagnostics={diagnostics}
                summary={summary}
              />

              {/* Cross-reference to the generic Reliability tab when PRE is enabled */}
              {hasPre && (
                <p className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="reliability-cross-ref">
                  Detailed PRE diagnostics (response certainty, sensitivity, individual uncertainty) are available in the <span className="font-medium">Reliability</span> tab.
                </p>
              )}

              {/* Stability factors from backend */}
              {stability?.factors?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Contributing Factors</p>
                  {stability.factors.map((f, i) => (
                    <p key={i} className="text-xs text-slate-500 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full flex-shrink-0" />
                      {f}
                    </p>
                  ))}
                </div>
              )}

              {/* Backend warnings (in addition to summary.warnings rendered inside ReliabilitySummary) */}
              {diagnostics?.warnings?.length > 0 && (
                <div className="pt-3 border-t border-slate-100 dark:border-slate-700 space-y-1.5">
                  <p className="text-xs font-medium text-warning-soft-foreground">Assumptions &amp; Warnings</p>
                  {diagnostics.warnings.map((w, i) => (
                    <p key={`d-${i}`} className="text-xs text-warning-soft-foreground flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                      {w.message}
                    </p>
                  ))}
                </div>
              )}

              {/* Data quality details */}
              {diagnostics?.data_quality && (
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                    <p className="text-[10px] text-slate-400">Sample Size</p>
                    <p className="font-semibold text-slate-700 dark:text-slate-200">{diagnostics.data_quality.sample_size}</p>
                  </div>
                  <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                    <p className="text-[10px] text-slate-400">Valid Responses</p>
                    <p className="font-semibold text-slate-700 dark:text-slate-200">{diagnostics.data_quality.valid_responses}</p>
                  </div>
                  <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                    <p className="text-[10px] text-slate-400">Response Rate</p>
                    <p className="font-semibold text-slate-700 dark:text-slate-200">{diagnostics.data_quality.response_rate}%</p>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-slate-100 dark:border-slate-700">
                <p className="text-[10px] text-slate-400">
                  Results are synthetic and directional. Segment data masked below 30 respondents.
                </p>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </section>
  );
}

// Pillar rendering moved to <ReliabilitySummary mode="full"/> in /components/reporting/ReliabilitySummary.jsx


// =============================================================================
// SHARED COMPONENTS
// =============================================================================

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-3">
      <h3 className="text-base font-bold text-slate-900 dark:text-white tracking-tight">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function RiskBadge({ level }) {
  const config = {
    severe: { bg: 'bg-negative-soft ', text: 'text-negative-soft-foreground ', border: 'border-negative-border ' },
    high:   { bg: 'bg-negative-soft ', text: 'text-negative-soft-foreground ', border: 'border-negative-border ' },
    medium: { bg: 'bg-warning-soft ', text: 'text-warning-soft-foreground ', border: 'border-warning-border ' },
    low:    { bg: 'bg-slate-50 dark:bg-slate-800', text: 'text-slate-500 dark:text-slate-400', border: 'border-slate-200 dark:border-border' },
  };
  const c = config[level] || config.low;

  return (
    <Badge variant="outline" className={cn("text-[10px] font-semibold", c.bg, c.text, c.border)}>
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </Badge>
  );
}

function StrengthBadge({ strength }) {
  const config = {
    strong:   { text: 'text-negative-soft-foreground ', bg: 'bg-negative-soft ', border: 'border-negative-border ' },
    moderate: { text: 'text-warning-soft-foreground ', bg: 'bg-warning-soft ', border: 'border-warning-border ' },
    weak:     { text: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800', border: 'border-slate-200 dark:border-border' },
    minimal:  { text: 'text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800', border: 'border-slate-100 dark:border-border' },
  };
  const c = config[strength] || config.minimal;

  return (
    <Badge variant="outline" className={cn("text-[10px] font-medium capitalize", c.text, c.bg, c.border)}>
      {strength}
    </Badge>
  );
}


// =============================================================================
// MEASUREMENT SETUP DIALOG
// =============================================================================

const OUTCOME_LABELS = {
  acceptance: 'Policy Acceptance',
  adoption_intent: 'Adoption Intent',
  fairness: 'Perceived Fairness',
  trust: 'Organizational Trust',
  workload: 'Workload Perception',
  retention_risk: 'Retention Risk',
  sentiment: 'Overall Sentiment',
  engagement: 'Employee Engagement',
  wellbeing: 'Wellbeing',
  inclusion: 'Inclusion',
  psychological_safety: 'Psychological Safety',
};

// Keywords to auto-detect HR outcome from stimulus name/prompt
const OUTCOME_KEYWORDS = {
  acceptance: ['accept', 'approve', 'support', 'agree', 'favorable', 'policy', 'change', 'proposal', 'new initiative'],
  adoption_intent: ['adopt', 'use', 'implement', 'will you', 'would you', 'likelihood', 'intent'],
  fairness: ['fair', 'equitable', 'just', 'equal', 'bias', 'discrimination'],
  trust: ['trust', 'confidence', 'rely', 'believe', 'credible', 'transparent', 'honest'],
  workload: ['workload', 'stress', 'burnout', 'overwhelm', 'capacity', 'bandwidth', 'hours', 'overtime'],
  retention_risk: ['leave', 'quit', 'resign', 'stay', 'retention', 'loyalty', 'career', 'job search'],
  sentiment: ['feel', 'opinion', 'think', 'view', 'reaction', 'response', 'general', 'overall'],
  engagement: ['engage', 'motivat', 'commit', 'dedicate', 'invest', 'energy', 'enthusiasm'],
  wellbeing: ['wellbeing', 'well-being', 'health', 'mental', 'wellness', 'balance', 'happy'],
  inclusion: ['inclus', 'belong', 'welcome', 'diverse', 'respect', 'voice', 'heard'],
  psychological_safety: ['safe', 'speak up', 'risk', 'mistake', 'comfortable', 'share', 'open'],
};

// Auto-detect outcome from stimulus text
function detectOutcome(stimulusName, promptText) {
  const text = `${stimulusName} ${promptText || ''}`.toLowerCase();
  
  let bestMatch = { outcome: 'sentiment', score: 0 };
  
  for (const [outcome, keywords] of Object.entries(OUTCOME_KEYWORDS)) {
    let score = 0;
    for (const keyword of keywords) {
      if (text.includes(keyword)) {
        score += keyword.length; // Longer matches = more confident
      }
    }
    if (score > bestMatch.score) {
      bestMatch = { outcome, score };
    }
  }
  
  return bestMatch;
}

function MeasurementSetupDialog({ measurementSetup, simulationId, onComplete }) {
  const [updating, setUpdating] = useState(false);
  const [selections, setSelections] = useState({});
  const [showWizard, setShowWizard] = useState(true);

  const stimuliNeedingAttention = measurementSetup?.stimuli_needing_attention || [];
  const allDefinitions = measurementSetup?.measure_definitions || [];

  // Auto-detect suggested mappings for all stimuli
  const suggestedMappings = allDefinitions.map(defn => {
    const detected = detectOutcome(defn.stimulus_name, defn.prompt_text);
    return {
      ...defn,
      suggested_outcome: detected.outcome,
      confidence: detected.score > 10 ? 'high' : detected.score > 5 ? 'medium' : 'low',
    };
  });

  const handleQuickSetup = async () => {
    setUpdating(true);
    try {
      const updates = suggestedMappings.map(defn => ({
        stimulus_id: defn.stimulus_id,
        hr_outcome_tag: selections[defn.stimulus_id]?.outcome || defn.suggested_outcome,
        confirm: true,
      }));
      await api.post(`/hrlab/report/${simulationId}/measurement-setup`, updates);
      onComplete();
    } catch (err) {
      console.error('Quick setup error:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirm = async () => {
    setUpdating(true);
    try {
      const updates = stimuliNeedingAttention.map(defn => ({
        stimulus_id: defn.stimulus_id,
        hr_outcome_tag: selections[defn.stimulus_id]?.outcome || defn.hr_outcome_tag,
        confirm: true,
      }));
      await api.post(`/hrlab/report/${simulationId}/measurement-setup`, updates);
      onComplete();
    } catch (err) {
      console.error('Measurement setup error:', err);
    } finally {
      setUpdating(false);
    }
  };

  // Quick Setup Wizard View
  if (showWizard && suggestedMappings.length > 0) {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-warning" />
            Quick Setup Wizard
          </DialogTitle>
          <DialogDescription>
            We've analyzed your stimuli and suggested HR outcome mappings. Review and apply with one click.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-3 max-h-[400px] overflow-y-auto">
          {suggestedMappings.map((defn, i) => {
            const selectedOutcome = selections[defn.stimulus_id]?.outcome || defn.suggested_outcome;
            return (
              <div 
                key={i} 
                className={cn(
                  "p-3 rounded-lg border transition-colors",
                  defn.confidence === 'high' ? "bg-positive-soft/50 border-positive-border" 
                    : defn.confidence === 'medium' ? "bg-blue-50/50 border-blue-200"
                    : "bg-slate-50 border-slate-200"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm text-slate-800 truncate">{defn.stimulus_name}</p>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-[9px] shrink-0",
                          defn.confidence === 'high' ? "text-positive-soft-foreground bg-positive-soft border-positive-border"
                            : defn.confidence === 'medium' ? "text-blue-700 bg-blue-100 border-blue-300"
                            : "text-slate-500 bg-slate-100 border-slate-300"
                        )}
                      >
                        {defn.confidence === 'high' ? '✓ High match' : defn.confidence === 'medium' ? '~ Likely match' : '? Manual review'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate" title={defn.prompt_text}>
                      {defn.prompt_text?.substring(0, 80) || 'No prompt text'}...
                    </p>
                  </div>
                  <Select
                    value={selectedOutcome}
                    onValueChange={(val) => setSelections(p => ({ ...p, [defn.stimulus_id]: { outcome: val } }))}
                  >
                    <SelectTrigger className="w-40 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(OUTCOME_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {key === defn.suggested_outcome && '⭐ '}{label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <Button variant="ghost" size="sm" onClick={() => setShowWizard(false)} className="text-xs text-slate-500">
            Manual Setup
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onComplete}>Skip</Button>
            <Button 
              onClick={handleQuickSetup} 
              disabled={updating} 
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Applying...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-1.5" />
                  Apply {suggestedMappings.length} Mapping{suggestedMappings.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Manual Setup View (existing)
  return (
    <>
      <DialogHeader>
        <DialogTitle>Measurement Setup</DialogTitle>
        <DialogDescription>Review how questions map to HR outcomes.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 py-4">
        {stimuliNeedingAttention.length > 0 ? (
          stimuliNeedingAttention.map((defn, i) => (
            <div key={i} className="p-3 bg-warning-soft/50 border border-warning-border rounded-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="font-medium text-sm text-slate-800">{defn.stimulus_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{defn.auto_mapping_reason}</p>
                </div>
                <Select
                  value={selections[defn.stimulus_id]?.outcome || defn.hr_outcome_tag}
                  onValueChange={(val) => setSelections(p => ({ ...p, [defn.stimulus_id]: { outcome: val } }))}
                >
                  <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(OUTCOME_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">
            All stimuli are already mapped. Use the Quick Setup Wizard to review.
          </p>
        )}
      </div>
      <DialogFooter className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setShowWizard(true)} className="text-xs text-warning-soft-foreground">
          <Zap className="w-3 h-3 mr-1" /> Try Quick Setup
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onComplete}>Skip</Button>
          <Button onClick={handleConfirm} disabled={updating || stimuliNeedingAttention.length === 0} className="bg-primary hover:bg-primary/90">
            {updating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Confirm
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
